import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminService, PriceCalculationLog } from '@app/core/services/admin.service';
import { ToastService } from '@app/core/services/toast.service';
import { catchError, retry, timeout, finalize } from 'rxjs/operators';
import { of } from 'rxjs';

interface ChartBar {
  label: string;
  value: number;
  percent: number;
}

@Component({
  selector: 'app-price-calculations',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './price-calculations.component.html',
  styleUrls: ['./price-calculations.component.scss']
})
export class PriceCalculationsComponent implements OnInit {
  calculations: PriceCalculationLog[] = [];
  loading = true;
  deletingId: string | null = null;

  totalCount = 0;
  avgPrice = 0;
  avgDuration = 0;
  avgDistance = 0;
  helperShare = 0;
  brandingShare = 0;

  dailyBars: ChartBar[] = [];
  durationBars: ChartBar[] = [];

  constructor(
    private adminService: AdminService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadCalculations();
  }

  loadCalculations() {
    this.loading = true;
    this.adminService.getPriceCalculations(500)
      .pipe(
        timeout(8000),
        retry({ count: 2, delay: 1000 }),
        catchError(() => {
          this.toastService.error('Fehler beim Laden der Kalkulationen');
          return of([] as PriceCalculationLog[]);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((logs) => {
        this.calculations = logs;
        this.computeStats();
        this.cdr.detectChanges();
      });
  }

  deleteCalculation(calc: PriceCalculationLog, event?: MouseEvent) {
    event?.stopPropagation();
    if (!calc.id || this.deletingId) return;

    this.deletingId = calc.id;
    this.adminService.deletePriceCalculation(calc.id)
      .pipe(
        finalize(() => {
          this.deletingId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          this.calculations = this.calculations.filter(c => c.id !== calc.id);
          this.computeStats();
          this.toastService.success('Kalkulation gelöscht');
        },
        error: () => {
          this.toastService.error('Fehler beim Löschen der Kalkulation');
        }
      });
  }

  private computeStats() {
    if (this.calculations.length === 0) {
      this.totalCount = 0;
      this.avgPrice = 0;
      this.avgDuration = 0;
      this.avgDistance = 0;
      this.helperShare = 0;
      this.brandingShare = 0;
      this.dailyBars = [];
      this.durationBars = [];
      return;
    }

    this.totalCount = this.calculations.length;
    const totalPrice = this.calculations.reduce((sum, c) => sum + (c.totalPrice || 0), 0);
    const totalDuration = this.calculations.reduce((sum, c) => sum + (c.durationDays || 0), 0);
    const totalDistance = this.calculations.reduce((sum, c) => sum + (c.distanceKm || 0), 0);

    this.avgPrice = Math.round(totalPrice / this.totalCount);
    this.avgDuration = Math.round((totalDuration / this.totalCount) * 10) / 10;
    this.avgDistance = Math.round((totalDistance / this.totalCount) * 10) / 10;

    const helperCount = this.calculations.filter(c => c.customerProvidesHelper).length;
    const brandingCount = this.calculations.filter(c => c.includes360Branding).length;
    this.helperShare = Math.round((helperCount / this.totalCount) * 100);
    this.brandingShare = Math.round((brandingCount / this.totalCount) * 100);

    this.dailyBars = this.buildDailyBars();
    this.durationBars = this.buildDurationBars();
  }

  private buildDailyBars(): ChartBar[] {
    const lastDays = 7;
    const map = new Map<string, number>();
    for (let i = lastDays - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const key = date.toISOString().slice(0, 10);
      map.set(key, 0);
    }

    this.calculations.forEach(c => {
      const key = new Date(c.createdAt).toISOString().slice(0, 10);
      if (map.has(key)) {
        map.set(key, (map.get(key) || 0) + 1);
      }
    });

    const max = Math.max(...Array.from(map.values()), 1);
    return Array.from(map.entries()).map(([date, count]) => ({
      label: date.slice(5),
      value: count,
      percent: Math.round((count / max) * 100)
    }));
  }

  private buildDurationBars(): ChartBar[] {
    const buckets = [
      { label: '1 Tag', min: 1, max: 1 },
      { label: '2 Tage', min: 2, max: 2 },
      { label: '3–4', min: 3, max: 4 },
      { label: '5–6', min: 5, max: 6 },
      { label: '7–13', min: 7, max: 13 },
      { label: '14–29', min: 14, max: 29 },
      { label: '30+', min: 30, max: 999 }
    ];

    const counts = buckets.map(() => 0);
    this.calculations.forEach(c => {
      const days = c.durationDays || 0;
      const idx = buckets.findIndex(b => days >= b.min && days <= b.max);
      if (idx >= 0) counts[idx] += 1;
    });

    const max = Math.max(...counts, 1);
    return buckets.map((b, idx) => ({
      label: b.label,
      value: counts[idx],
      percent: Math.round((counts[idx] / max) * 100)
    }));
  }

  formatDate(value: string) {
    const date = new Date(value);
    return date.toLocaleString('de-DE');
  }

  formatDateOnly(value: string) {
    const date = new Date(value);
    return date.toLocaleDateString('de-DE');
  }
}
